/**
 * BattleCharacterSprites.ts - 32x32 battle sprites for party members
 *
 * Each character class has multiple poses:
 * - Idle: Standing battle stance
 * - Attack: Weapon raised/swing
 * - Cast: Magic casting pose
 * - Hurt: Taking damage
 * - Victory: Celebrating
 * - Dead: Fallen
 *
 * Layout: 6 classes × 6 poses = 36 sprites in a 6x6 grid (192x192)
 */

export enum BattlePose {
  IDLE = 0,
  ATTACK = 1,
  CAST = 2,
  HURT = 3,
  VICTORY = 4,
  DEAD = 5,
}

export class BattleCharacterSprites {
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  /**
   * Generate all battle character sprites
   * Returns a 192x192 canvas (6 classes × 32px, 6 poses × 32px)
   */
  generateBattleSprites(): HTMLCanvasElement {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 192;  // 6 poses × 32px
    this.canvas.height = 192; // 6 classes × 32px
    this.ctx = this.canvas.getContext('2d')!;

    // Clear with transparency
    this.ctx.clearRect(0, 0, 192, 192);

    // Generate sprites for each class
    const classes = ['FIGHTER', 'THIEF', 'MONK', 'RED_MAGE', 'WHITE_MAGE', 'BLACK_MAGE'];
    classes.forEach((className, classIndex) => {
      for (let pose = 0; pose < 6; pose++) {
        const x = pose * 32;
        const y = classIndex * 32;
        this.drawCharacter(className, pose as BattlePose, x, y);
      }
    });

    return this.canvas;
  }

  private drawCharacter(className: string, pose: BattlePose, x: number, y: number): void {
    if (!this.ctx) return;

    switch (className) {
      case 'FIGHTER':
        this.drawFighter(x, y, pose);
        break;
      case 'THIEF':
        this.drawThief(x, y, pose);
        break;
      case 'MONK':
        this.drawMonk(x, y, pose);
        break;
      case 'RED_MAGE':
        this.drawRedMage(x, y, pose);
        break;
      case 'WHITE_MAGE':
        this.drawWhiteMage(x, y, pose);
        break;
      case 'BLACK_MAGE':
        this.drawBlackMage(x, y, pose);
        break;
    }
  }

  // =========================================================================
  // FIGHTER - Red armor, sword and shield
  // =========================================================================
  private drawFighter(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    // Base colors
    const armorRed = '#cc2222';
    const armorDark = '#991111';
    const skin = '#ffcc99';
    const helmet = '#888888';
    const sword = '#cccccc';
    const swordHilt = '#664422';

    // Legs (adjusted for pose)
    ctx.fillStyle = armorDark;
    if (pose === BattlePose.DEAD) {
      // Lying down
      ctx.fillRect(x + 4, y + 20, 24, 4);
    } else if (pose === BattlePose.ATTACK) {
      // Lunging
      ctx.fillRect(x + 8, y + 22, 5, 8);
      ctx.fillRect(x + 18, y + 24, 5, 6);
    } else {
      ctx.fillRect(x + 10, y + 22, 5, 8);
      ctx.fillRect(x + 17, y + 22, 5, 8);
    }

    // Feet
    ctx.fillStyle = '#444444';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 28, 6, 3);
      ctx.fillRect(x + 17, y + 28, 6, 3);
    }

    // Body
    ctx.fillStyle = armorRed;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 14, 20, 6);
    } else {
      ctx.fillRect(x + 8, y + 12, 16, 12);
    }

    // Shoulders
    ctx.fillStyle = armorDark;
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 5, y + 12, 4, 4);
      ctx.fillRect(x + 23, y + 12, 4, 4);
    }

    // Head
    ctx.fillStyle = skin;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 14, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 4, 10, 9);
    }

    // Helmet
    ctx.fillStyle = helmet;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 12, 6, 4);
    } else {
      ctx.fillRect(x + 10, y + 2, 12, 5);
      ctx.fillRect(x + 11, y + 6, 10, 2);
      // Helmet plume
      ctx.fillStyle = '#ff4444';
      ctx.fillRect(x + 14, y + 0, 4, 3);
    }

    // Eyes
    ctx.fillStyle = '#000000';
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 16, 2, 1);
    } else if (pose === BattlePose.HURT) {
      ctx.fillRect(x + 13, y + 7, 2, 1);
      ctx.fillRect(x + 17, y + 7, 2, 1);
    } else {
      ctx.fillRect(x + 13, y + 7, 2, 2);
      ctx.fillRect(x + 17, y + 7, 2, 2);
    }

    // Sword
    ctx.fillStyle = sword;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 2, y + 8, 12, 2);
    } else if (pose === BattlePose.ATTACK) {
      // Sword raised high
      ctx.fillRect(x + 24, y + 2, 3, 14);
      ctx.fillStyle = swordHilt;
      ctx.fillRect(x + 23, y + 14, 5, 3);
    } else if (pose === BattlePose.VICTORY) {
      // Sword raised in victory
      ctx.fillRect(x + 20, y + 0, 3, 12);
      ctx.fillStyle = swordHilt;
      ctx.fillRect(x + 19, y + 10, 5, 3);
    } else {
      // Idle/other - sword at side
      ctx.fillRect(x + 25, y + 8, 3, 12);
      ctx.fillStyle = swordHilt;
      ctx.fillRect(x + 24, y + 18, 5, 3);
    }

    // Shield (left arm)
    if (pose !== BattlePose.DEAD) {
      ctx.fillStyle = '#666666';
      ctx.fillRect(x + 2, y + 12, 6, 10);
      ctx.fillStyle = '#888888';
      ctx.fillRect(x + 3, y + 13, 4, 8);
      ctx.fillStyle = armorRed;
      ctx.fillRect(x + 4, y + 15, 2, 4);
    }

    // Cast pose - magic aura
    if (pose === BattlePose.CAST) {
      ctx.fillStyle = 'rgba(255, 255, 0, 0.3)';
      ctx.fillRect(x + 4, y + 4, 24, 24);
    }
  }

  // =========================================================================
  // THIEF - Green outfit, dagger
  // =========================================================================
  private drawThief(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    const clothGreen = '#44aa44';
    const clothDark = '#226622';
    const skin = '#ffcc99';
    const dagger = '#aaaaaa';

    // Legs
    ctx.fillStyle = clothDark;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 20, 24, 4);
    } else if (pose === BattlePose.ATTACK) {
      // Crouching attack
      ctx.fillRect(x + 6, y + 24, 5, 6);
      ctx.fillRect(x + 20, y + 22, 5, 8);
    } else {
      ctx.fillRect(x + 10, y + 22, 5, 8);
      ctx.fillRect(x + 17, y + 22, 5, 8);
    }

    // Feet
    ctx.fillStyle = '#442200';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 28, 6, 3);
      ctx.fillRect(x + 17, y + 28, 6, 3);
    }

    // Body
    ctx.fillStyle = clothGreen;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 14, 20, 6);
    } else {
      ctx.fillRect(x + 8, y + 12, 16, 12);
    }

    // Belt
    ctx.fillStyle = '#664422';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 8, y + 20, 16, 2);
    }

    // Head
    ctx.fillStyle = skin;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 14, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 4, 10, 9);
    }

    // Hood
    ctx.fillStyle = clothDark;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 12, 6, 3);
    } else {
      ctx.fillRect(x + 9, y + 2, 14, 5);
      ctx.fillRect(x + 9, y + 5, 3, 4);
      ctx.fillRect(x + 20, y + 5, 3, 4);
    }

    // Eyes
    ctx.fillStyle = '#000000';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 13, y + 7, 2, 2);
      ctx.fillRect(x + 17, y + 7, 2, 2);
    }

    // Dagger
    ctx.fillStyle = dagger;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 2, y + 10, 8, 2);
    } else if (pose === BattlePose.ATTACK) {
      // Dagger thrust forward
      ctx.fillRect(x + 24, y + 14, 8, 2);
      ctx.fillStyle = '#664422';
      ctx.fillRect(x + 22, y + 13, 3, 4);
    } else if (pose === BattlePose.VICTORY) {
      // Dagger raised
      ctx.fillRect(x + 22, y + 4, 2, 8);
    } else {
      ctx.fillRect(x + 24, y + 16, 2, 6);
      ctx.fillStyle = '#664422';
      ctx.fillRect(x + 23, y + 20, 4, 3);
    }
  }

  // =========================================================================
  // MONK - Orange gi, bare fists
  // =========================================================================
  private drawMonk(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    const giOrange = '#ffaa00';
    const giDark = '#cc8800';
    const skin = '#ffcc99';

    // Legs
    ctx.fillStyle = giDark;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 20, 24, 4);
    } else if (pose === BattlePose.ATTACK) {
      // Kick pose
      ctx.fillRect(x + 10, y + 20, 5, 10);
      ctx.fillRect(x + 18, y + 16, 12, 4);
    } else {
      ctx.fillRect(x + 10, y + 22, 5, 8);
      ctx.fillRect(x + 17, y + 22, 5, 8);
    }

    // Feet
    ctx.fillStyle = skin;
    if (pose !== BattlePose.DEAD) {
      if (pose === BattlePose.ATTACK) {
        ctx.fillRect(x + 9, y + 28, 6, 3);
        ctx.fillRect(x + 28, y + 16, 4, 4);
      } else {
        ctx.fillRect(x + 9, y + 28, 6, 3);
        ctx.fillRect(x + 17, y + 28, 6, 3);
      }
    }

    // Body
    ctx.fillStyle = giOrange;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 14, 20, 6);
    } else {
      ctx.fillRect(x + 8, y + 10, 16, 14);
    }

    // Belt
    ctx.fillStyle = '#000000';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 8, y + 18, 16, 2);
    }

    // Arms/Fists
    ctx.fillStyle = skin;
    if (pose === BattlePose.ATTACK) {
      // Punch forward
      ctx.fillRect(x + 4, y + 12, 4, 4);
      ctx.fillRect(x + 24, y + 10, 8, 5);
    } else if (pose === BattlePose.VICTORY) {
      // Arms raised
      ctx.fillRect(x + 4, y + 6, 4, 4);
      ctx.fillRect(x + 24, y + 6, 4, 4);
    } else if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 14, 4, 4);
      ctx.fillRect(x + 24, y + 14, 4, 4);
    }

    // Head
    ctx.fillStyle = skin;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 14, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 2, 10, 9);
    }

    // Headband
    ctx.fillStyle = giOrange;
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 10, y + 4, 12, 2);
    }

    // Eyes
    ctx.fillStyle = '#000000';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 13, y + 6, 2, 2);
      ctx.fillRect(x + 17, y + 6, 2, 2);
    }
  }

  // =========================================================================
  // RED MAGE - Red/white outfit, rapier and magic
  // =========================================================================
  private drawRedMage(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    const cloakRed = '#ff4488';
    const cloakDark = '#cc2266';
    const skin = '#ffcc99';
    const rapier = '#cccccc';

    // Legs
    ctx.fillStyle = cloakDark;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 22, 24, 4);
    } else {
      ctx.fillRect(x + 10, y + 22, 5, 8);
      ctx.fillRect(x + 17, y + 22, 5, 8);
    }

    // Feet
    ctx.fillStyle = '#442200';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 28, 6, 3);
      ctx.fillRect(x + 17, y + 28, 6, 3);
    }

    // Cape/Body
    ctx.fillStyle = cloakRed;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 14, 22, 8);
    } else {
      ctx.fillRect(x + 6, y + 10, 20, 14);
      // Cape flowing
      ctx.fillRect(x + 2, y + 14, 6, 10);
    }

    // Head
    ctx.fillStyle = skin;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 14, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 6, 10, 8);
    }

    // Hat
    ctx.fillStyle = cloakRed;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 10, 6, 5);
    } else {
      ctx.fillRect(x + 9, y + 2, 14, 6);
      ctx.fillRect(x + 11, y + 0, 10, 3);
      // White feather
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x + 20, y + 0, 3, 5);
    }

    // Eyes
    ctx.fillStyle = '#000000';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 13, y + 9, 2, 2);
      ctx.fillRect(x + 17, y + 9, 2, 2);
    }

    // Rapier
    ctx.fillStyle = rapier;
    if (pose === BattlePose.ATTACK) {
      // Thrust
      ctx.fillRect(x + 24, y + 12, 8, 2);
    } else if (pose === BattlePose.CAST) {
      // Raised for casting
      ctx.fillRect(x + 22, y + 2, 2, 10);
      // Magic sparkles
      ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
      ctx.fillRect(x + 4, y + 4, 3, 3);
      ctx.fillRect(x + 8, y + 8, 2, 2);
    } else if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 24, y + 10, 2, 10);
    }
  }

  // =========================================================================
  // WHITE MAGE - White robes, healing staff
  // =========================================================================
  private drawWhiteMage(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    const robeWhite = '#ffffff';
    const robeBorder = '#ff4444';
    const skin = '#ffcc99';
    const staff = '#ccaa00';

    // Legs (hidden by robe)
    ctx.fillStyle = robeWhite;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 18, 24, 6);
    } else {
      ctx.fillRect(x + 8, y + 18, 16, 12);
    }

    // Feet
    ctx.fillStyle = '#aaaaaa';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 28, 6, 3);
      ctx.fillRect(x + 17, y + 28, 6, 3);
    }

    // Robe body
    ctx.fillStyle = robeWhite;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 12, 22, 8);
    } else {
      ctx.fillRect(x + 6, y + 8, 20, 14);
    }

    // Robe trim
    ctx.fillStyle = robeBorder;
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 6, y + 8, 20, 2);
      ctx.fillRect(x + 6, y + 20, 20, 2);
    }

    // Head
    ctx.fillStyle = skin;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 12, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 4, 10, 6);
    }

    // Hood
    ctx.fillStyle = robeWhite;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 8, 6, 5);
    } else {
      ctx.fillRect(x + 9, y + 2, 14, 5);
      ctx.fillRect(x + 9, y + 5, 3, 3);
      ctx.fillRect(x + 20, y + 5, 3, 3);
      // Cross on hood
      ctx.fillStyle = robeBorder;
      ctx.fillRect(x + 14, y + 2, 4, 1);
      ctx.fillRect(x + 15, y + 1, 2, 3);
    }

    // Eyes
    ctx.fillStyle = '#000000';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 13, y + 6, 2, 2);
      ctx.fillRect(x + 17, y + 6, 2, 2);
    }

    // Staff
    ctx.fillStyle = staff;
    if (pose === BattlePose.CAST) {
      // Raised for healing
      ctx.fillRect(x + 4, y + 4, 2, 16);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x + 2, y + 2, 6, 4);
      ctx.fillRect(x + 4, y + 0, 2, 3);
      // Healing glow
      ctx.fillStyle = 'rgba(0, 255, 128, 0.4)';
      ctx.fillRect(x + 0, y + 0, 10, 10);
    } else if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 8, 2, 14);
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x + 2, y + 6, 6, 4);
      ctx.fillRect(x + 4, y + 4, 2, 3);
    }
  }

  // =========================================================================
  // BLACK MAGE - Blue robes, shadowed face, magic staff
  // =========================================================================
  private drawBlackMage(x: number, y: number, pose: BattlePose): void {
    const ctx = this.ctx!;

    const robeBlue = '#4444ff';
    const robeDark = '#2222aa';
    const staff = '#664422';

    // Legs (hidden by robe)
    ctx.fillStyle = robeDark;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 18, 24, 6);
    } else {
      ctx.fillRect(x + 8, y + 18, 16, 12);
    }

    // Feet
    ctx.fillStyle = '#222244';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 28, 6, 3);
      ctx.fillRect(x + 17, y + 28, 6, 3);
    }

    // Robe body
    ctx.fillStyle = robeBlue;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 4, y + 12, 22, 8);
    } else {
      ctx.fillRect(x + 6, y + 8, 20, 14);
    }

    // Face (shadowed)
    ctx.fillStyle = '#000000';
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 12, 6, 6);
    } else {
      ctx.fillRect(x + 11, y + 6, 10, 6);
    }

    // Glowing eyes
    ctx.fillStyle = '#ffff00';
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 13, y + 8, 2, 2);
      ctx.fillRect(x + 17, y + 8, 2, 2);
    }

    // Hat
    ctx.fillStyle = robeBlue;
    if (pose === BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 6, 6, 7);
    } else {
      ctx.fillRect(x + 10, y + 4, 12, 4);
      ctx.fillRect(x + 12, y + 2, 8, 3);
      ctx.fillRect(x + 14, y + 0, 4, 3);
    }

    // Hat brim shadow
    ctx.fillStyle = robeDark;
    if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 9, y + 7, 14, 2);
    }

    // Staff
    ctx.fillStyle = staff;
    if (pose === BattlePose.CAST) {
      // Raised for casting
      ctx.fillRect(x + 24, y + 2, 2, 16);
      // Magic orb
      ctx.fillStyle = '#8888ff';
      ctx.fillRect(x + 22, y + 0, 6, 4);
      ctx.fillStyle = '#aaaaff';
      ctx.fillRect(x + 23, y + 1, 4, 2);
      // Magic aura
      ctx.fillStyle = 'rgba(128, 128, 255, 0.4)';
      ctx.fillRect(x + 18, y + 0, 14, 10);
    } else if (pose === BattlePose.ATTACK) {
      // Staff forward
      ctx.fillRect(x + 24, y + 8, 2, 12);
      ctx.fillStyle = '#8888ff';
      ctx.fillRect(x + 22, y + 6, 6, 4);
    } else if (pose !== BattlePose.DEAD) {
      ctx.fillRect(x + 26, y + 6, 2, 14);
      ctx.fillStyle = '#8888ff';
      ctx.fillRect(x + 24, y + 4, 6, 4);
      ctx.fillStyle = '#aaaaff';
      ctx.fillRect(x + 25, y + 5, 4, 2);
    }
  }

  /**
   * Get a specific sprite from the generated canvas
   */
  getSprite(className: string, pose: BattlePose): { x: number; y: number; width: number; height: number } | null {
    const classIndex = ['FIGHTER', 'THIEF', 'MONK', 'RED_MAGE', 'WHITE_MAGE', 'BLACK_MAGE'].indexOf(className);
    if (classIndex === -1) return null;

    return {
      x: pose * 32,
      y: classIndex * 32,
      width: 32,
      height: 32,
    };
  }
}

// Singleton instance
let battleSpritesInstance: BattleCharacterSprites | null = null;

export function getBattleCharacterSprites(): BattleCharacterSprites {
  if (!battleSpritesInstance) {
    battleSpritesInstance = new BattleCharacterSprites();
  }
  return battleSpritesInstance;
}
